import { action, computed } from '@ember/object';
import { isNone } from '@ember/utils';
import AbstractCondition, { addError } from './abstract';
import { anyEmail } from 'cio/app-models/conditions/email';
import RefinementCondition from './refinement';
import { MessagingChannelType } from 'cio/utils/setup-guide/messaging-channels';

export function any(type: string) {
  if (type === 'email') {
    return anyEmail;
  }

  return `${type}_*`;
}

export enum SpecificLinkOption {
  None = 'none',
  Exact = 'eq',
  Contains = 'contains',
}

const MAX_IN_APP_ACTION_NAME_LENGTH = 100;

class ActionCondition extends AbstractCondition {
  type = 'action';

  trackedAttributes = ['action', 'eventType', 'inverse', 'linkHref', 'specificLink', 'isForeignEvent'];

  @computed('refinement')
  get children() {
    return [this.refinement];
  }

  @computed('isAnyInParent', 'specificLink', 'linkHref')
  get errors() {
    let errors = {};

    if (this.specificLink !== SpecificLinkOption.None) {
      switch (this.type) {
        case MessagingChannelType.Email:
          if (isNone(this.linkHref) || this.linkHref.length === 0) {
            addError(errors, 'linkHref', 'You must enter a URL to match');
          }
          break;
        case MessagingChannelType.InApp:
          if (isNone(this.linkHref) || this.linkHref.length === 0) {
            addError(errors, 'linkHref', 'You must enter an action name to match');
          }

          if ((this.linkHref ?? '').length > MAX_IN_APP_ACTION_NAME_LENGTH) {
            addError(
              errors,
              'linkHref',
              `The action name must be less than or equal to ${MAX_IN_APP_ACTION_NAME_LENGTH} characters`,
            );
          }
          break;
      }
    }

    return errors;
  }

  @computed('action', 'isAnyInParent')
  get isAny() {
    return this.action === any(this.type) || this.isAnyInParent;
  }

  @computed('action')
  get isAnyInParent() {
    return this.action?.includes('any_') && this.action !== any(this.type);
  }

  @computed('isAnyInParent', 'isRefinable')
  get canRefine() {
    if (!this.isAnyInParent) {
      return true;
    }

    return this.isRefinable;
  }

  action: string | null = null;
  defaultToWildcard = false;
  eventType = 'sent_action';
  inverse = false;
  specificLink = SpecificLinkOption.None;
  linkHref: string | null = null; // when specificLink is not `none`; this holds the URL to match
  refinement: RefinementCondition | null = null;
  isForeignEvent: boolean | undefined = undefined;

  @computed('email')
  get isVariation() {
    return this.action?.includes('variation');
  }

  init() {
    // newsletter types are formatted as "CHANNEL_newsletter"
    if (this.type.endsWith('_newsletter')) {
      let typeName = this.type.substring(0, this.type.indexOf('_newsletter'));
      this.set('type', typeName);
    }

    if (isNone(this.action)) {
      // Will always default to wildcard if the action type is email.
      // Other types may be disabled (in the case of filtering people)
      if (!this.defaultToWildcard && this.type !== 'email') {
        this.set('action', undefined);
      } else {
        this.set('action', any(this.type));
      }
    }

    super.init();
  }

  @action
  duplicate() {
    return ActionCondition.create({
      _event: this.event,
      type: this.type,
      eventType: this.eventType,
      inverse: this.inverse,
      specificLink: this.specificLink,
      linkHref: this.linkHref,
      refinement: this.refinement?.duplicate(),
      action: this.action,
      defaultToWildcard: this.defaultToWildcard,
      isForeignEvent: this.isForeignEvent,
    });
  }

  toString() {
    return 'ActionCondition';
  }
}

export default ActionCondition;
